import { connect } from 'react-redux';
import { getNavigationStats } from '../../actionCreators/stats';
import { problemsColumns } from '../../tableColumns/problemColumns.jsx';
import { changeTab, history } from '../../utils';
import PageContainer from '../../components/Redesing/page-container';
import { PaginationTable } from '../../components/Redesing/table/pagination-table';
import {
  FilterChips,
  IColumn,
  ISortConfig,
} from '../../ts/interface/components/redesing/table.interface';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card as CardMui } from '@mui/material';
import {
  MovaticFieldGroupAsyncSelect,
  MovaticFieldGroupSelectFilter,
} from '../../components/Redesing/movatic-field-group';
import { SearchChip } from '../../ts/interface/components/table.interface';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { getCredentials } from '../../api/http';
import { DEFAULT_ROWS_PER_PAGE } from '../../constants';
import { useParams } from 'react-router-dom';
import PageContent from '../../components/Redesing/page-content';
import ProblemsTags from './problems-tags';
import useUrlState from '../../hooks/use-url-state';
import useSearchDebounce from '../../hooks/search/useSearchDebounce';
import CustomAsyncToast from '../../components/Redesing/custom-async-toast';
import useExportProblems from '../../hooks/problems/query/useExportProblems';
import {
  useSupportTicketsRelayQuery,
  mapRelaySupportTicketsToIProblems,
  useTagsRelayQuery,
  mapRelayTagsToITags,
} from '../../hooks/supportTickets/query/V2';
import { useKeysetPagination } from '../../hooks/use-keyset-pagination';
import AddIcon from '@mui/icons-material/Add';
import MovaticCustomModal from '../../components/Modal/MovaticCustomModal';
import IconButtonMenu from '../../components/Redesing/icon-button-menu';
import { MovaticFieldGroupText } from '../../components/Redesing/movatic-field-group';
import CustomToast from '../../components/Redesing/custom-toast';
import useSmallScreen from '../../hooks/useSmallScreen';
import { createSupportTicket } from '../../api/graphql/support-tickets';
import UserSearch, { type SelectedUser } from '../../components/coupons/UserSearch';

const filterOptions = [
  { value: 0, label: 'Open', disabled: true },
  { value: 1, label: 'Closed' },
];

interface IFilters {
  label: string;
  value: string;
}

function Problems(props: { systemLoaded: boolean; movaticAccess: number }) {
  // Opening a ticket on a rider's behalf is Movatic-side support work, not
  // something a partner admin does for their own system.
  const canCreateTicket = Boolean(props.movaticAccess);
  const { currentTab = 'tickets' } = useParams<{ currentTab: string }>();
  const [currentTabState, setCurrentTabState] = useState(currentTab);

  // Shared server-side keyset pagination (page# kept local, not in the URL).
  const {
    pageNumber,
    after,
    isPaginating,
    onPageChange,
    onPageLoaded,
    reset: resetPagination,
  } = useKeysetPagination();
  const [pageState, setPageState, isComplete, isRestoring] = useUrlState({
    sizePerPage: DEFAULT_ROWS_PER_PAGE,
    search: '',
    tagValues: [] as IFilters[],
    selectedStatus: {
      label: 'Open',
      value: '0',
    } as IFilters | null,
    sortingConfig: {
      key: 'created',
      direction: 'desc',
    } as ISortConfig,
  });

  const { systemGid, systemId } = getCredentials();
  const isSmallScreen = useSmallScreen();

  // CS opens these by hand: riders reach support over chat now, and the app
  // no longer submits tickets the way the legacy one did.
  const [newTicketModal, setNewTicketModal] = useState<boolean>(false);
  const [newTicketContent, setNewTicketContent] = useState<string>('');
  const [newTicketUser, setNewTicketUser] = useState<SelectedUser | null>(null);
  const debouncedSearchTerm = useSearchDebounce(pageState.search);

  // Use GraphQL hook for tags
  const { data: tagsData, isLoading: tagsLoading } = useTagsRelayQuery(
    {
      systemId: systemGid,
      archived: false, // Only show non-archived tags
    },
    { enabled: props.systemLoaded }
  );

  // Map GraphQL tags to ITag format
  const mappedTags = useMemo(
    () => (tagsData ? mapRelayTagsToITags(tagsData) : []),
    [tagsData]
  );

  const loadedTags = useMemo(
    () =>
      mappedTags.map((item) => {
        return {
          label: item.name,
          value: item.name,
        };
      }),
    [mappedTags]
  );

  const [chips, setChips] = useState<SearchChip[]>([]);

  const [filterChips, setFilterChips] = useState<FilterChips>({
    status: ['0'],
    tags: [],
  } as FilterChips);

  // `value` is a derived alias of the persisted status, so the query, dropdown,
  // and chips all read the same URL-persisted source (refresh-safe).
  const value = pageState.selectedStatus;
  const [open, setOpen] = React.useState(false);

  // Rebuild the chip list from the applied, URL-persisted filters so the chips
  // always match the fetched data — after a refresh and when a filter is
  // cleared. Skip while the URL state is still being restored.
  useEffect(() => {
    if (isRestoring) return;

    const next: SearchChip[] = [];
    if (pageState.selectedStatus?.value) {
      next.push({
        label: 'Status',
        field: 'status',
        value: pageState.selectedStatus.value,
        displayValue: pageState.selectedStatus.label,
        disabled: false,
      });
    }
    pageState.tagValues?.forEach((tag) => {
      next.push({
        label: 'Tags',
        field: 'tags',
        value: tag.value,
        displayValue: tag.label,
      });
    });
    setChips(next);
  }, [isRestoring, pageState.selectedStatus, pageState.tagValues]);

  useEffect(() => {
    if (currentTab !== currentTabState) {
      setCurrentTabState(currentTab);
    }
  }, [currentTab, currentTabState]);
  const handleTabsChange = useCallback(
    (event: React.SyntheticEvent, value: React.SetStateAction<string>) => {
      changeTab(value, currentTab, 'problems');
    },
    [currentTab]
  );

  const isEnabled = useMemo(() => {
    return isComplete && props.systemLoaded;
  }, [props.systemLoaded, isComplete]);

  // Convert sort config to GraphQL format
  const sortConfig = useMemo(() => {
    const sortKeyMap: Record<string, 'CREATED' | 'STATE' | 'CONTENT'> = {
      created: 'CREATED',
      state: 'STATE',
      content: 'CONTENT',
    };
    const field = sortKeyMap[pageState.sortingConfig.key] || 'CREATED';
    const order = pageState.sortingConfig.direction === 'asc' ? 'ASC' : 'DESC';
    return [{ field, order: order as 'ASC' | 'DESC' }];
  }, [pageState.sortingConfig.key, pageState.sortingConfig.direction]);

  // Use GraphQL hook for support tickets
  const {
    data: ticketsData,
    isLoading: loadingProblems,
    isRefetching: fetchingProblems,
    hasMore: graphqlHasMore,
    count: totalProblems,
    endCursor,
    refetch: refetchTickets,
  } = useSupportTicketsRelayQuery(
    {
      systemId: systemGid,
      state: value?.value !== undefined ? parseInt(value.value, 10) : undefined,
      tags:
        pageState.tagValues.length > 0
          ? pageState.tagValues.map((item) => item.value)
          : undefined,
      pattern: debouncedSearchTerm || undefined,
    },
    {
      enabled: isEnabled,
      limit: pageState.sizePerPage,
      sort: sortConfig,
      after,
    }
  );

  // Only the current page's rows are shown (server-side pagination).
  const problemsItems = useMemo(
    () => (ticketsData ? mapRelaySupportTicketsToIProblems(ticketsData) : []),
    [ticketsData]
  );

  // Record the next page's cursor and clear the paginating loader when a fetch
  // settles.
  useEffect(() => {
    onPageLoaded(endCursor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketsData]);

  const { mutateAsync: exportMutation } = useExportProblems();

  const handleExportData = useCallback(() => {
    CustomAsyncToast({
      promise: () =>
        exportMutation({
          search: debouncedSearchTerm || undefined,
          status: value?.value,
          order: pageState.sortingConfig.key,
          ordinance: pageState.sortingConfig.direction,
          tags:
            pageState.tagValues.length > 0
              ? pageState.tagValues.map((item) => item.value)
              : undefined,
        }),
      successMessage: () => 'Support tickets exported successfully',
      loadingMessage: 'Exporting support tickets...',
      errorMessage: 'Error exporting support tickets',
    });
  }, [exportMutation, debouncedSearchTerm, value?.value, pageState]);

  const handleChipsUpdate = useCallback(() => {
    const filtersAux: FilterChips = {
      status: [],
      tags: [],
    };
    chips.forEach((chip) => {
      switch (chip.field) {
        case 'status':
          // @ts-ignore
          filtersAux.status.push(chip.value);
          break;
        case 'tags':
          // @ts-ignore
          filtersAux.tags.push(chip.value);
          break;
        default:
          break;
      }
    });
    setFilterChips(filtersAux);
  }, [chips]);

  const handleStatusChange = useCallback(
    (values: string[], list: any[], metadata: { label: string; value: string }): void => {
      resetPagination();
      setPageState((prevState) => ({
        ...prevState,
        selectedStatus: list.find((item) => item.value === values[0]),
      }));
      setChips((prevChips) => {
        const valuesFound: string[] = [];

        const newChips = prevChips.filter((chip) => {
          if (chip.field !== metadata.value) {
            return true;
          }

          const found = values.includes(chip.value as string);

          if (found) {
            valuesFound.push(chip.value as string);
          }

          return found;
        });

        if (values.length === valuesFound.length) {
          return newChips;
        }

        values.forEach((value) => {
          if (!valuesFound.includes(value)) {
            const option = list.find((option) => option.value.toString() === value);
            newChips.push({
              label: metadata.label,
              field: metadata.value,
              value,
              displayValue: option!.label,
            });
          }
        });
        return newChips;
      });
    },
    [setPageState, setChips, resetPagination]
  );

  useEffect(() => {
    handleChipsUpdate();
  }, [chips, handleChipsUpdate]);

  const handleChipDelete = useCallback(
    (deletedGroup: SearchChip) => {
      resetPagination();
      setChips((prevChips) => {
        return prevChips.filter((group) => {
          return group.label !== deletedGroup.label;
        });
      });

      if (deletedGroup.label === 'Status') {
        setPageState((prevState) => ({
          ...prevState,
          selectedStatus: null,
        }));
      }
      if (deletedGroup.label === 'Tags') {
        setPageState((prevState) => ({
          ...prevState,
          tagValues: [],
        }));
      }
      handleChipsUpdate();
    },
    [handleChipsUpdate, setPageState, resetPagination]
  );

  const contentNew = () => {
    return (
      <CardMui>
        <PaginationTable
          isLoadingCache={fetchingProblems}
          padding={{
            pr: 1,
            pt: 1,
          }}
          buttons={[
            {
              label: 'Export',
              onClick: handleExportData,
              startIcon: <FileDownloadIcon />,
            },
            ...(canCreateTicket
              ? [
                  {
                    startIcon: <AddIcon />,
                    label: 'Add',
                    dataId: 'add-support-ticket-button',
                    onClick: () => setNewTicketModal(true),
                  },
                ]
              : []),
          ]}
          customKey={'id'}
          onRowCLick={(id: string) => history.push(`/problems/${id}`)}
          sortDirection={'desc'}
          showFilter
          chips={chips}
          handleChipDelete={handleChipDelete}
          onApplyFilter={() => {
            if (value?.value !== filterChips.status?.[0]) {
              handleStatusChange(
                [
                  ...filterOptions
                    .filter((option) => value?.label === option.label)
                    .map((item) => item.value.toString()),
                ],
                filterOptions,
                { label: 'Status', value: 'status' }
              );
            }

            if (
              JSON.stringify(pageState.tagValues) !== JSON.stringify(filterChips.tags)
            ) {
              handleStatusChange(
                pageState.tagValues.map((item) => item.value.toString()),
                loadedTags,
                { label: 'Tags', value: 'tags' }
              );
            }
          }}
          spaceFilters={
            <>
              <MovaticFieldGroupAsyncSelect
                dataId="Status-single-select"
                label={'Status'}
                description="Select the status to filter the support tickets."
                isLoading={tagsLoading}
                name={'status_select'}
                id={'status_select'}
                options={filterOptions}
                showClearButton={true}
                value={value ? { value: value.value, label: value.label } : null}
                onChange={(
                  event: React.SyntheticEvent,
                  newValue: { value: string; label: string }
                ) => {
                  // The status drives the query directly, so reset pagination
                  // to avoid paginating with a stale keyset cursor. Persist it
                  // in URL state so it survives a refresh.
                  resetPagination();
                  setPageState((prevState) => ({
                    ...prevState,
                    selectedStatus: newValue,
                  }));
                }}
                open={open}
                onOpen={() => {
                  setOpen(true);
                }}
                onClose={() => {
                  setOpen(false);
                }}
                isOptionEqualToValue={(option: any, value: any) =>
                  option.value === value.value
                }
                getOptionText={(option: any) => option.label}
                handleSearch={() => {}}
              />
              <MovaticFieldGroupSelectFilter
                label={'Tags'}
                isLoading={tagsLoading}
                value={pageState.tagValues}
                onChange={(event, newValue) => {
                  // Tags drive the query directly, so reset pagination to
                  // avoid paginating with a stale keyset cursor.
                  resetPagination();
                  setPageState((prevState) => ({
                    ...prevState,
                    tagValues: [
                      ...newValue.filter((option) =>
                        loadedTags.some(
                          (item: { label: string }) => item.label === option.label
                        )
                      ),
                    ],
                  }));
                }}
                options={loadedTags}
              />
            </>
          }
          sortBy={pageState.sortingConfig.key}
          onSortChange={(sort) => {
            resetPagination();
            setPageState((prevState) => ({
              ...prevState,
              sortingConfig: {
                key: sort.key,
                direction: sort.direction as 'asc' | 'desc',
              },
            }));
          }}
          handleSearch={(search) => {
            resetPagination();
            setPageState((prevState) => ({
              ...prevState,
              search: search.target.value,
            }));
          }}
          searchText={pageState.search}
          columns={problemsColumns() as IColumn[]}
          items={problemsItems || []}
          totalItems={totalProblems}
          onPageChange={(page, pageN) => onPageChange(pageN)}
          onRowsPerPageChange={(row) => {
            const pageSize = Number(row.target.value);
            resetPagination();
            setPageState((prevState) => ({
              ...prevState,
              sizePerPage: pageSize,
            }));
          }}
          page={pageNumber}
          rowsPerPage={pageState.sizePerPage}
          searchPlaceholder={'Search'}
          noDataText={`No support tickets`}
          noDataSlice
          localSearch={false}
          hasMore={graphqlHasMore}
          isLoading={
            loadingProblems || !props.systemLoaded || !isComplete || isPaginating
          }
        />
      </CardMui>
    );
  };

  return (
    <PageContainer spaceName={'Support Tickets'}>
      <PageContent
        detailedView={false}
        currentTabV={currentTabState}
        handleTabsChange={handleTabsChange}
        tabsList={[
          {
            label: 'Tickets',
            value: 'tickets',
          },
          {
            label: 'Tags',
            value: 'tags',
          },
        ]}
        tabsChildren={
          <>
            {currentTabState === 'tickets' && contentNew()}
            {currentTabState === 'tags' && <ProblemsTags />}
          </>
        }
      />
      <MovaticCustomModal
        customStyles={{ content: { minWidth: 'none' } }}
        sx={{
          '& .MuiDialog-paper': {
            maxWidth: isSmallScreen ? '90vw' : '40vw',
            margin: isSmallScreen ? 1 : 'auto',
            width: '100%',
          },
        }}
        open={newTicketModal && canCreateTicket}
        onClose={() => setNewTicketModal(false)}
        title={'New Support Ticket'}
        content={
          <>
            <MovaticFieldGroupText
              label="What did the rider report?"
              placeholder="Paste or summarise what they told you"
              description="(Required)"
              name="content"
              value={newTicketContent}
              dataId="new-ticket-content"
              multiline
              rows={5}
              onChange={(e) => setNewTicketContent(e.target.value)}
            />
            <UserSearch
              systemId={systemGid}
              description="(Required) Links the ticket to the rider's account"
              selectedUser={
                newTicketUser
                  ? { value: newTicketUser.id, label: newTicketUser.label }
                  : null
              }
              onChange={(user) => setNewTicketUser(user)}
            />
          </>
        }
        action={
          <IconButtonMenu
            buttons={[
              {
                label: 'Cancel',
                onClick: () => setNewTicketModal(false),
              },
              {
                label: 'Create',
                dataId: 'save-support-ticket-button',
                onClick: () => {
                  if (!newTicketUser) {
                    CustomToast({
                      type: 'error',
                      message: 'Please choose the rider this is about',
                    });
                    return;
                  }
                  if (!newTicketContent.trim()) {
                    CustomToast({
                      type: 'error',
                      message: 'Please describe what the rider reported',
                    });
                    return;
                  }

                  CustomAsyncToast({
                    promise: () =>
                      createSupportTicket({
                        systemOldId: systemId ? String(systemId) : undefined,
                        content: newTicketContent.trim(),
                        userOldId: newTicketUser.oldId,
                      }),
                    successMessage: () => 'Ticket created',
                    errorMessage:
                      'Oops! An error has occurred while creating the ticket. Please try again later',
                    loadingMessage: 'Creating ticket...',
                  }).then((created) => {
                    if (created) {
                      refetchTickets();
                      setNewTicketContent('');
                      setNewTicketUser(null);
                      setNewTicketModal(false);
                    }
                  });
                },
              },
            ]}
          />
        }
      />
    </PageContainer>
  );
}

export default connect(
  (state: any) => ({
    systemLoaded: state.system.isLoaded,
    movaticAccess: state.admin.admin.admin.movaticAccess,
  }),
  (dispatch: Function) => ({
    getNavigationStats() {
      dispatch(getNavigationStats());
    },
  })
)(Problems);
